import { STYLES, THEMES } from "@dentacore/ui";
import { useCallback, useId, useRef, useState } from "react";

import { useAuth } from "../auth/auth-context";
import { useTheme } from "../theme/theme-context";
import { useDismiss } from "../ui/use-dismiss";
import styles from "./shell.module.css";

/**
 * Who is signed in, the theme, the style, and Sign out — behind the person's
 * initials (decision CS9; the Glass / Solid style joined the theme in CF-D6).
 *
 * These three used to take the sidebar's foot and a toggle in the banner. None
 * of them is used more than once a session, and the foot disappeared entirely
 * when the sidebar was collapsed, so Sign out was unreachable there. Behind
 * the initials they are one click away on every screen, collapsed or not.
 *
 * **Not `role="menu"`.** A menu is a list of commands navigated with arrow
 * keys; this holds an identity, a two-button toggle and a button, so it is a
 * disclosure — a button with `aria-expanded` and an ordinary panel that Tab
 * walks through.
 */
export function AccountMenu(): React.JSX.Element {
  const { user, signOut } = useAuth();
  const { theme, setTheme, style, setStyle } = useTheme();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  const close = useCallback((how: "outside" | "escape") => {
    setOpen(false);
    // Escape sends focus back to the control that opened it, so the next Tab
    // starts from there. A press elsewhere leaves it where it landed.
    if (how === "escape") button.current?.focus();
  }, []);
  useDismiss(open, close, wrap);

  const name = [user?.firstName, user?.lastName].filter(Boolean).join(" ");

  return (
    <div className={styles.account} ref={wrap}>
      <button
        ref={button}
        type="button"
        className={styles.avatar}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={name === "" ? "Account" : `Account — ${name}`}
        onClick={() => {
          setOpen((was) => !was);
        }}
        data-testid="account-menu-button"
      >
        {initialsOf(user?.firstName, user?.lastName)}
      </button>

      {open && (
        <div id={panelId} className={styles.accountPanel} data-testid="account-menu">
          {/* The id means "the signed-in identity", so it wraps both lines —
              see the note that used to sit on the sidebar foot. */}
          <div className={styles.accountWho} data-testid="signed-in-as">
            <div className={styles.userName}>{name}</div>
            <div className={styles.userEmail} title={user?.email}>
              {user?.email}
            </div>
          </div>

          <div className={styles.accountRow}>
            <span>Theme</span>
            <div className={styles.themeToggle} role="group" aria-label="Theme">
              {THEMES.map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={theme === option}
                  onClick={() => setTheme(option)}
                >
                  {option === "light" ? "Light" : "Dark"}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.accountRow}>
            <span>Style</span>
            <div className={styles.themeToggle} role="group" aria-label="Style">
              {STYLES.map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={style === option}
                  onClick={() => setStyle(option)}
                >
                  {option === "glass" ? "Glass" : "Solid"}
                </button>
              ))}
            </div>
          </div>

          <button type="button" className={styles.signOut} onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

/** "TA" for Tala Aoun; one letter when there is one name; "?" before sign-in resolves. */
function initialsOf(first: string | undefined, last: string | undefined): string {
  const letters = [first, last]
    .map((part) => part?.trim().charAt(0) ?? "")
    .join("")
    .toUpperCase();
  return letters === "" ? "?" : letters;
}
